import { z } from 'zod';
import { FirestoreDocId, WeekStartDate } from '../eos/scorecard.js';
/**
 * Metric drill — per-measurable read response for `/scorecard/:measurableId`.
 *
 * The Scorecard surface lists every measurable as a one-row sparkline.
 * Drilling into a row promotes it to its own page (deep-linkable so a
 * teammate can paste the URL into Slack and land directly on the metric
 * being discussed) with three sections:
 *
 *   1. **Trend chart** — the `history` window (default 13 weeks; nullable
 *      values where a week was skipped so the renderer can draw a gap).
 *   2. **Compare rail** — the four period-over-period deltas
 *      (`w/w` · `m/m` · `q/q` · `y/y`). Each delta is the difference
 *      between the latest value and the value `N` weeks back, expressed
 *      both in absolute units and as a percentage. Direction is derived
 *      so the UI can paint the right color without re-implementing the
 *      sign convention.
 *   3. **Drill rows** — up to 20 rows of "the things inside this number"
 *      (e.g. for a "Pipeline coverage" measurable, the open opportunities
 *      that compose it). When the measurable has no connector pipeline
 *      wired yet, `drillRows` is empty and `emptyReason` explains why —
 *      honest per the "no mock data" rule.
 *
 * The response is READ-shaped — the route is a `GET`, no audit event.
 */
/**
 * The four time-period buckets the compare rail reports against.
 * Closed string set — promoted to a Zod enum so server + client agree
 * on the bucket labels without hand-typing the same literal twice.
 *
 * - `w/w` — week-over-week (latest vs. 1 week prior)
 * - `m/m` — month-over-month (latest vs. 4 weeks prior)
 * - `q/q` — quarter-over-quarter (latest vs. 13 weeks prior)
 * - `y/y` — year-over-year (latest vs. 52 weeks prior)
 */
export const MetricComparePeriod = z.enum(['w/w', 'm/m', 'q/q', 'y/y']);
/**
 * Closed set of directions for a compare delta. Drives the color the
 * compare rail paints — the UI doesn't re-derive sign arithmetic.
 *
 * - `up`   — latest is greater than the prior period
 * - `down` — latest is less than the prior period
 * - `flat` — equal, or the prior period has no measurement (delta=0)
 */
export const MetricCompareDirection = z.enum(['up', 'down', 'flat']);
/**
 * One bucket on the compare rail. `absolute` is the raw delta in the
 * measurable's unit; `percent` is the same delta as a share of the prior
 * value (`(current - prior) / prior * 100`) — signed so the UI can show
 * `-12.5%` without re-deriving the sign. Both fields are `0` when there
 * isn't enough history to compute the bucket; the route still emits the
 * row so the rail layout stays stable across measurables.
 */
export const MetricCompareDelta = z.object({
    period: MetricComparePeriod,
    /** Current value minus prior-period value, in the measurable's unit. */
    absolute: z.number(),
    /** `(absolute / prior) * 100`; signed. `0` when prior was zero/missing. */
    percent: z.number(),
    /** Derived direction the UI paints — saves a re-implementation client-side. */
    direction: MetricCompareDirection,
});
/**
 * Connector kinds that can be the upstream source for a drill row, plus
 * two derived markers (`manual` for hand-entered numbers, `derived` for
 * formulas computed over other measurables). Mirrors `ConnectorKind` plus
 * the two extra markers so the schema is self-contained — drill rows can
 * arrive without a connector kind being installed.
 */
export const MetricDrillSourceKind = z.enum([
    'salesforce',
    'stripe',
    'github',
    'gmail',
    'slack',
    'manual',
    'derived',
]);
/**
 * One row in the drill-through table. Shape stays generic — the page
 * renders a plain `label : value : meta` grid — because the underlying
 * rows differ per connector (Salesforce opportunities, Stripe invoices,
 * GitHub PRs). When the row references a known entity in the graph the
 * `label` is the entity name and the page wraps it in `<EntityName>`
 * for the resolution-confidence pill.
 */
export const MetricDrillRow = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    /** Numeric or pre-formatted string — the connector picks the right shape. */
    value: z.union([z.number(), z.string()]),
    /** Optional secondary text (e.g. "Salesforce · stage 4", "this week"). */
    meta: z.string().optional(),
});
/**
 * Provenance for the drill response — the upstream system whose data
 * feeds this measurable. Renders through `<SourceBadge>` on the page.
 * Optional because not every measurable is connector-fed; manual rows
 * omit this field.
 */
export const MetricDrillSource = z.object({
    kind: MetricDrillSourceKind,
    /** Human label rendered on the badge (e.g. "Salesforce · pipeline"). */
    label: z.string().min(1),
});
/**
 * One point on the trend chart. `value` is `null` for weeks where no
 * measurement was recorded — the renderer draws a gap rather than
 * interpolating, which would lie about the data.
 */
export const MetricHistoryPoint = z.object({
    /** ISO date for the Monday or Sunday that anchors the week. */
    weekOf: WeekStartDate,
    value: z.number().nullable(),
});
/** Hard cap on `drillRows` payload size (mirrors the doc comment). */
export const METRIC_DRILL_ROWS_MAX = 20;
/**
 * Shape returned by `GET /api/orgs/:o/measurables/:measurableId/drill`.
 *
 * `goal` / `current` / `unit` mirror the corresponding fields on the
 * parent `Measurable` for display convenience — the page renders the
 * one-line header (`"Pipeline coverage · 3.30×"`) from these three
 * fields and falls back to a goal-less header when they're absent.
 *
 * `emptyReason` is populated when `history` is empty (e.g. a freshly
 * created measurable with no values yet) OR when `drillRows` is empty
 * and we want to explain why (e.g. "connector pipeline not yet wired").
 * The UI surfaces the reason in the empty-state body.
 */
export const MetricDrillResponse = z.object({
    measurableId: FirestoreDocId,
    measurableName: z.string().min(1),
    /** The goal threshold value from the measurable; absent for `'=='` goals when omitted. */
    goal: z.number().optional(),
    /** The latest measurement value, when one exists. */
    current: z.number().optional(),
    /** Free-form unit string from the measurable's goal (e.g. "%", "×", "$"). */
    unit: z.string().optional(),
    /** Trend chart points — oldest → newest. Default 13 weeks. */
    history: z.array(MetricHistoryPoint),
    /** Compare rail — always 4 rows (w/w, m/m, q/q, y/y), zero-filled when prior data is missing. */
    compare: z.array(MetricCompareDelta),
    /** Up to {@link METRIC_DRILL_ROWS_MAX} rows; empty until a connector pipeline is wired. */
    drillRows: z.array(MetricDrillRow).max(METRIC_DRILL_ROWS_MAX),
    source: MetricDrillSource.optional(),
    /** Why `history` / `drillRows` are empty — surfaced in the page's empty state. */
    emptyReason: z.string().optional(),
});
